import { CoreImageView } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import clsx from 'clsx';
import React from 'react';
import { getInlineStyles } from '../../../utils/styles';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import { Tag } from '../../UI/Tag/Tag';
import { TextCommon } from '../../UI/Text/TextCommon';
import styles from './paysendCard.module.css';

type TPaysendCard = {
    maskedPan?: string;
    isCardScreen?: boolean;
    isFrozen?: boolean;
    isExpired?: boolean;
    isKycInProgress?: boolean;
    appleWalletAdded?: boolean;
    googleWalletAdded?: boolean;
    getCard?: string;
    metaText?: string;
    isFirstRender?: boolean;
    isCardIssued?: boolean;
};

export const PaysendCard = ({
    maskedPan,
    isCardScreen,
    isFrozen,
    isExpired,
    isKycInProgress,
    appleWalletAdded,
    googleWalletAdded,
    metaText,
    getCard,
    isFirstRender,
    isCardIssued,
}: TPaysendCard) => {
    const isInactive = isFrozen || isExpired || isKycInProgress;
    return (
        <div
            className={clsx(
                styles.cardContainer,
                isCardScreen && styles.cardContainerOnCardScreen,
                isCardScreen ? styles.cardAnimationZoomIn : styles.cardAnimationZoomOut,
                isFrozen && styles.frozen,
                isExpired && styles.expired,
                isFirstRender && styles.firstRender,
                isInactive && styles.cardContainerInactive,
            )}
            style={getInlineStyles({
                variables: {
                    'frozen-background-image': `url(${process.env.NX_PUBLIC_CLOUD_PAYSEND_API}/images/web/ps-3.0/frozen_card_bg.webp)`,
                    'expired-background-image': `url(${process.env.NX_PUBLIC_CLOUD_PAYSEND_API}/images/web/ps-3.0/expired_card_bg.webp)`,
                },
            })}
        >
            <div className={clsx(styles.cardRow, styles.topAlignment, styles.spaceBetween)}>
                <div className={clsx(styles.psIconContainer, isInactive && styles.psIconContainerInactive)}>
                    <CoreImageView
                        src={getHref('paysend')}
                        styleProps={{
                            width: isCardScreen ? 35 : 24,
                            height: isCardScreen ? 35 : 24,
                            iconColor: `var(--color-white_base)`,
                        }}
                    />
                </div>
                {isInactive && (
                    <div className={styles.tagContainer}>
                        <Tag metaVariant={'tag'} metaHierarchy={'inverted'} size={isCardScreen ? 'default' : 'small'} metaText={metaText} />
                    </div>
                )}
            </div>
            <div className={clsx(styles.cardRow, isCardIssued && styles.spaceBetween)}>
                <ConditionalRender condition={isCardIssued}>
                    <TextCommon
                        isWidget={false}
                        content={`•• ${maskedPan}`}
                        options={{
                            variant: isCardScreen ? 'main' : 'secondary',
                            textColor: isFrozen || isExpired ? 'white_40_tr_base' : 'white_base',
                        }}
                    />
                </ConditionalRender>
                <ConditionalRender condition={!isCardIssued && !isKycInProgress}>
                    <TextCommon
                        isWidget={false}
                        content={getCard}
                        options={{
                            variant: 'subheader',
                            textColor: 'white_base',
                        }}
                        as={'span'}
                    />
                    <VisualView iconName={'ic_arrow_forward'} iconHierarchy={'emphasised'} />
                </ConditionalRender>
                <ConditionalRender condition={isCardIssued}>
                    <div className={clsx(styles.paymentIconsRow, isCardScreen && styles.paymentIconsRowCardScreen)}>
                        {appleWalletAdded && (
                            <div className={styles.iconContainer}>
                                <CoreImageView
                                    src={getHref(isFrozen || isExpired ? 'ic_apple_pay_small_opacity' : 'ic_apple_pay_small')}
                                    styleProps={{
                                        width: isCardScreen ? 32 : 21,
                                        iconColor: `var(--color-purple_100)`,
                                    }}
                                />
                            </div>
                        )}
                        {googleWalletAdded && (
                            <div className={styles.iconContainer}>
                                <CoreImageView
                                    src={getHref(isFrozen || isExpired ? 'ic_google_pay_small_opacity' : 'ic_google_pay_small')}
                                    styleProps={{
                                        width: isCardScreen ? 38 : 25,
                                        iconColor: `var(--color-purple_100)`,
                                    }}
                                />
                            </div>
                        )}
                        <div className={styles.iconContainer}>
                            <CoreImageView
                                src={getHref(isFrozen || isExpired ? 'mastercard_white_opacity' : 'mastercard')}
                                styleProps={{
                                    width: isCardScreen ? 40 : 27,
                                    iconColor: `var(--color-purple_100)`,
                                }}
                            />
                        </div>
                    </div>
                </ConditionalRender>
            </div>
        </div>
    );
};
